import type { PlanDraft } from "@weights/plan-reading";
import { sameChips } from "./draft.js";
import { type Change, change } from "./patch.js";
import { labelKey } from "./lib.js";
import { WEEKDAYS } from "./dates.js";
import { fold } from "./text.js";

export interface WeekDiff {
  /** `${r}:${columnId}` → last week's chips, for every cell that differs. */
  cells: Map<string, string[]>;
  /** Label keys whose definition is new or reworded. */
  definitions: Set<string>;
  changedDefinitions: string[];
  newDefinitions: string[];
  removedDefinitions: string[];
  newColumns: string[];
  removedColumns: string[];
  locations: Set<number>;
  /** Note titles whose text is new, reworded or gone. */
  notes: string[];
  /** The week's title before, when it is compared and differs. */
  title: string | null;
}

/** What the Changes view compares the week with: the week it was copied from, or what is published. */
export type Baseline = "last" | "published";

export const baselineName: Record<Baseline, string> = { last: "Last week", published: "Published" };
export const sinceText: Record<Baseline, string> = { last: "since last week", published: "since published" };

export const cellKey = (r: number, columnID: string) => `${r}:${columnID}`;

/** A column of this week is last week's when it kept its id, else when it kept its name. */
function counterpart(base: PlanDraft, column: { id: string; name: string }) {
  return base.columns.find((c) => c.id === column.id) ?? base.columns.find((c) => fold(c.name) === fold(column.name));
}

/** `title` compares the titles too: a copied week is renamed on purpose, a published one is not. */
export function diffWeeks(draft: PlanDraft, base: PlanDraft, { title = false }: { title?: boolean } = {}): WeekDiff {
  const out: WeekDiff = {
    cells: new Map(),
    definitions: new Set(),
    changedDefinitions: [],
    newDefinitions: [],
    removedDefinitions: [],
    newColumns: [],
    removedColumns: [],
    locations: new Set(),
    notes: [],
    title: title && draft.title !== base.title ? base.title : null,
  };
  const matched = new Set<string>();
  for (const column of draft.columns) {
    const old = counterpart(base, column);
    if (old) matched.add(old.id);
    else out.newColumns.push(column.name);
    for (let r = 0; r < 7; r++) {
      const now = draft.days[r]?.cells[column.id] ?? [];
      const then = old ? (base.days[r]?.cells[old.id] ?? []) : [];
      if (!sameChips(now, then)) out.cells.set(cellKey(r, column.id), then);
    }
  }
  for (const column of base.columns) if (!matched.has(column.id)) out.removedColumns.push(column.name);
  for (let r = 0; r < 7; r++) if ((draft.days[r]?.location ?? "") !== (base.days[r]?.location ?? "")) out.locations.add(r);

  const before = new Map(base.sessions.map((s) => [labelKey(s.label), s.lines]));
  const nowKeys = new Set<string>();
  for (const s of draft.sessions) {
    const key = labelKey(s.label);
    nowKeys.add(key);
    const then = before.get(key);
    if (!then) {
      out.definitions.add(key);
      out.newDefinitions.push(s.label);
    } else if (!sameChips(then, s.lines)) {
      out.definitions.add(key);
      out.changedDefinitions.push(s.label);
    }
  }
  for (const s of base.sessions) if (!nowKeys.has(labelKey(s.label))) out.removedDefinitions.push(s.label);

  const notesBefore = new Map(base.notes.map((n) => [fold(n.title), n.lines]));
  const notesNow = new Set<string>();
  for (const n of draft.notes) {
    notesNow.add(fold(n.title));
    const then = notesBefore.get(fold(n.title));
    if (!then || !sameChips(then, n.lines)) out.notes.push(n.title || "Untitled note");
  }
  for (const n of base.notes) if (!notesNow.has(fold(n.title))) out.notes.push(n.title || "Untitled note");
  return out;
}

export function diffCount(diff: WeekDiff): number {
  return (
    diff.cells.size +
    diff.definitions.size +
    diff.removedDefinitions.length +
    diff.newColumns.length +
    diff.removedColumns.length +
    diff.locations.size +
    diff.notes.length +
    (diff.title !== null ? 1 : 0)
  );
}

export type ChangeItem =
  | { kind: "cell"; r: number; c: number; text: string; was: string[]; now: string[] }
  | { kind: "definition"; label: string; text: string; status: "new" | "changed" | "removed" }
  | { kind: "other"; text: string };

/** Puts a reworded or removed definition back as the baseline has it. */
export function revertDefinition(draft: PlanDraft, base: PlanDraft, label: string): Change[] {
  const key = labelKey(label);
  const was = base.sessions.find((s) => labelKey(s.label) === key);
  if (!was) return [];
  const i = draft.sessions.findIndex((s) => labelKey(s.label) === key);
  return i >= 0 ? [change(draft, ["sessions", i], was)] : [change(draft, ["sessions"], [...draft.sessions, was])];
}

/** Every change, in the order the sheet reads (cells by day, then definitions, then the rest), for lists the coach can act on. */
export function changeItems(diff: WeekDiff, draft: PlanDraft): ChangeItem[] {
  const out: ChangeItem[] = [];
  for (let r = 0; r < 7; r++)
    draft.columns.forEach((column, c) => {
      const was = diff.cells.get(cellKey(r, column.id));
      if (was) out.push({ kind: "cell", r, c, text: `${WEEKDAYS[r]} · ${column.name}`, was, now: draft.days[r]?.cells[column.id] ?? [] });
    });
  for (const label of diff.newDefinitions) out.push({ kind: "definition", label, text: label, status: "new" });
  for (const label of diff.changedDefinitions) out.push({ kind: "definition", label, text: label, status: "changed" });
  for (const label of diff.removedDefinitions) out.push({ kind: "definition", label, text: label, status: "removed" });
  if (diff.newColumns.length) out.push({ kind: "other", text: `New columns: ${diff.newColumns.join(", ")}` });
  if (diff.removedColumns.length) out.push({ kind: "other", text: `Columns removed: ${diff.removedColumns.join(", ")}` });
  if (diff.locations.size) out.push({ kind: "other", text: `Locations: ${[...diff.locations].map((r) => WEEKDAYS[r]).join(", ")}` });
  if (diff.notes.length) out.push({ kind: "other", text: `Notes: ${diff.notes.join(", ")}` });
  if (diff.title !== null) out.push({ kind: "other", text: `Title, was ${diff.title || "untitled"}` });
  return out;
}

const plural = (n: number, one: string, many = one + "s") => `${n} ${n === 1 ? one : many}`;

/** "3 cells, 1 definition": the headline counts. */
export function changeCounts(diff: WeekDiff): string {
  const parts: string[] = [];
  if (diff.cells.size) parts.push(plural(diff.cells.size, "cell"));
  const defs = diff.definitions.size + diff.removedDefinitions.length;
  if (defs) parts.push(plural(defs, "definition"));
  const columns = diff.newColumns.length + diff.removedColumns.length;
  if (columns) parts.push(plural(columns, "column"));
  if (diff.locations.size) parts.push(plural(diff.locations.size, "location"));
  if (diff.notes.length) parts.push(plural(diff.notes.length, "note"));
  if (diff.title !== null) parts.push("the title");
  return parts.join(", ");
}
